// Owner: workflow-canvas (plan §9.33, §4.2). Two editor scopes. The canvas scope is active only while
// focus is inside the canvas (`focus: 'within'`), so keys in the inspector or on a toolbar button never
// delete, rename or move a step (Enter on Publish stays Publish's). The builder scope covers the whole
// builder (top bar, palette, canvas, inspector): undo, redo and ⌘S work wherever focus rests, except
// while typing in a field (the field's own undo). The standard verbs come from `editCommands`; the
// canvas adds arrows along the graph, Enter, `/` (step palette), R (rename), zoom and fit, ⇧⌘L.
import {
  type CommandDef,
  type EditActions,
  defineCommand,
  defineScope,
  editCommands,
} from '@dataroom/interaction';
import { defineAppCommands } from '../../../extensions/commands';
import { saveNowCommand } from '../../../editor-kit/commands';
import { t } from '../../../lib/i18n';

export type Direction = 'up' | 'down' | 'left' | 'right';

export type WorkflowCanvasContext = EditActions & {
  readOnly: () => boolean;
  /** Clipboard verbs for the command bar; the keys themselves stay native copy, cut and paste events. */
  copy: () => void;
  cut: () => void;
  paste: () => void;
  navigate: (direction: Direction) => void;
  open: () => void;
  palette: () => void;
  rename: () => void;
  /** `shift+1` (a printable chord) stays with the canvas; the ⌘ view keys are the builder's. */
  fit: () => void;
  saveNow: () => Promise<boolean>;
};

export type WorkflowBuilderContext = EditActions & {
  readOnly: () => boolean;
  saveNow: () => Promise<boolean>;
  fit: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  actualSize: () => void;
  relayout: () => void;
};

export const workflowBuilderScope = defineScope<WorkflowBuilderContext>({
  id: 'workflow.builder',
  kind: 'editor',
  title: () => t('wc.scope.builder'),
});

export const workflowCanvasScope = defineScope<WorkflowCanvasContext>({
  id: 'workflow.canvas',
  kind: 'editor',
  title: () => t('interaction.scope.workflowCanvas'),
});

const view = () => t('wc.cmd.groupView');
const edit = () => t('wc.cmd.groupEdit');

const arrows: Array<[Direction, string]> = [
  ['up', 'arrowup'],
  ['down', 'arrowdown'],
  ['left', 'arrowleft'],
  ['right', 'arrowright'],
];

/** Keys that need a selection or a history stay in the shortcut sheet (greyed) while they cannot run. */
const alwaysListed = <C>(command: CommandDef<C>): CommandDef<C> => ({ ...command, listed: 'always' });

/**
 * Copy, cut and paste: the keys stay the browser's own events (`useClipboardSource` / `Target` get
 * them, nothing is intercepted), the commands list them in the sheet and run them from the command
 * bar.
 */
const clipboardCommand = (
  name: 'copy' | 'cut' | 'paste',
  shortcut: string,
  when: (c: WorkflowCanvasContext) => boolean,
) =>
  defineCommand<WorkflowCanvasContext>({
    id: `workflow.canvas.${name}`,
    title: () => t(name === 'copy' ? 'wc.action.copy' : name === 'cut' ? 'wc.action.cut' : 'wc.paste'),
    ...(name === 'copy' ? { icon: 'copy' as const } : {}),
    shortcut,
    // On the canvas ⌘C copies steps (a record peek's "copy link" is another screen's).
    ...(name === 'copy' ? { overrides: ['records.peek.copyLink'] } : {}),
    scope: workflowCanvasScope,
    group: edit,
    listed: 'always',
    when,
    run: (c, info) => {
      // Typed on the keyboard: the browser's own copy, cut or paste event follows.
      if (info.source === 'keyboard') return false;
      c[name]();
    },
  });

/**
 * Undo, redo, save and the view keys (fit, zoom): on the whole builder, wherever focus rests. Undo and redo
 * give way to a text field's own; the view chords are `textSafe` (⌘0 types nothing), so they also work
 * from the palette search.
 */
export const workflowBuilderCommands = [
  ...editCommands(workflowBuilderScope, ['undo', 'redo']).map(alwaysListed),
  saveNowCommand(workflowBuilderScope),
  defineCommand<WorkflowBuilderContext>({
    id: 'workflow.canvas.fit',
    title: () => t('wc.fit'),
    shortcut: 'mod+0',
    scope: workflowBuilderScope,
    textSafe: true,
    group: view,
    run: (c) => c.fit(),
  }),
  defineCommand<WorkflowBuilderContext>({
    id: 'workflow.canvas.actualSize',
    title: () => t('wc.actualSize'),
    shortcut: 'mod+1',
    scope: workflowBuilderScope,
    textSafe: true,
    group: view,
    run: (c) => c.actualSize(),
  }),
  defineCommand<WorkflowBuilderContext>({
    id: 'workflow.canvas.zoomIn',
    title: () => t('wc.zoomIn'),
    shortcut: 'mod+=',
    scope: workflowBuilderScope,
    textSafe: true,
    group: view,
    run: (c) => c.zoomIn(),
  }),
  defineCommand<WorkflowBuilderContext>({
    id: 'workflow.canvas.zoomOut',
    title: () => t('wc.zoomOut'),
    shortcut: 'mod+-',
    scope: workflowBuilderScope,
    textSafe: true,
    group: view,
    run: (c) => c.zoomOut(),
  }),
  defineCommand<WorkflowBuilderContext>({
    id: 'workflow.canvas.relayout',
    title: () => t('wc.relayout'),
    shortcut: 'mod+shift+l',
    scope: workflowBuilderScope,
    textSafe: true,
    group: view,
    run: (c) => c.relayout(),
  }),
];

export const workflowCanvasCommands = [
  ...editCommands(workflowCanvasScope, ['duplicate', 'selectAll', 'escape']).map(alwaysListed),
  clipboardCommand('copy', 'mod+c', (c) => (c.hasSelection ? c.hasSelection() : false)),
  clipboardCommand('cut', 'mod+x', (c) => !c.readOnly() && (c.hasSelection ? c.hasSelection() : false)),
  clipboardCommand('paste', 'mod+v', (c) => !c.readOnly()),
  // Delete / Backspace delete the selected steps (with Undo); the id is pinned by the keyboard map test.
  defineCommand<WorkflowCanvasContext>({
    id: 'workflow.deleteStep',
    title: () => t('interaction.cmd.deleteStep'),
    icon: 'trash',
    shortcut: ['delete', 'backspace'],
    scope: workflowCanvasScope,
    group: edit,
    listed: 'always',
    when: (c) => !c.readOnly() && (c.hasSelection ? c.hasSelection() : false),
    run: (c) => c.deleteSelection?.(),
  }),
  defineCommand<WorkflowCanvasContext>({
    id: 'workflow.canvas.open',
    title: () => t('wc.cmd.open'),
    shortcut: 'enter',
    scope: workflowCanvasScope,
    group: edit,
    listed: 'always',
    when: (c) => (c.hasSelection ? c.hasSelection() : false),
    run: (c) => c.open(),
  }),
  defineCommand<WorkflowCanvasContext>({
    id: 'workflow.canvas.palette',
    title: () => t('wf.b.addStep'),
    icon: 'plus',
    shortcut: '/',
    // On the canvas "/" opens the step palette (there is no search field to focus).
    overrides: ['app.search'],
    scope: workflowCanvasScope,
    group: edit,
    when: (c) => !c.readOnly(),
    run: (c) => c.palette(),
  }),
  defineCommand<WorkflowCanvasContext>({
    id: 'workflow.canvas.rename',
    title: () => t('wc.action.rename'),
    shortcut: 'r',
    scope: workflowCanvasScope,
    group: edit,
    listed: 'always',
    when: (c) => !c.readOnly() && (c.hasSelection ? c.hasSelection() : false),
    run: (c) => c.rename(),
  }),
  ...arrows.map(([direction, key]) =>
    defineCommand<WorkflowCanvasContext>({
      id: `workflow.canvas.go.${direction}`,
      title: () => t(`wc.cmd.go.${direction}`),
      shortcut: key,
      scope: workflowCanvasScope,
      group: view,
      repeat: true,
      run: (c) => c.navigate(direction),
    }),
  ),
  // `shift+1` types "!" in a text field, so it stays a canvas key (⌘0 below works from the palette).
  defineCommand<WorkflowCanvasContext>({
    id: 'workflow.canvas.fitKey',
    title: () => t('wc.fit'),
    shortcut: 'shift+1',
    scope: workflowCanvasScope,
    group: view,
    hidden: true,
    run: (c) => c.fit(),
  }),
];

export default defineAppCommands({
  id: 'workflows.builder',
  loadTitles: () => import('../messages'),
  scopes: [workflowBuilderScope, workflowCanvasScope],
  commands: [...workflowBuilderCommands, ...workflowCanvasCommands],
});
